"use client";
import { Button } from "../ui/button";
import {
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  writeFileOverwolf,
  saveFile,
  useSettingsStore,
  openFileOrFiles,
  FiltersConfig,
  isApp,
  isOverwolf,
  setTheme,
  useTheme,
  type Theme,
} from "@repo/lib";
import { useT } from "../(providers)";
import { Label } from "../ui/label";
import { toast } from "sonner";
import { ReactNode } from "react";
import { Switch } from "../ui/switch";
import { ScrollArea } from "../ui/scroll-area";
import { Separator } from "../ui/separator";
import { Input } from "../ui/input";
import { IconSizes } from "./icon-sizes";
import { ColorPicker } from "./color-picker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Slider } from "../ui/slider";
import { ProfileManager } from "./profile-manager";
import { Play, RotateCcw, X } from "lucide-react";
import { playAlertSound, ALERT_SOUND_OPTIONS } from "./audio-alert";
import { Section } from "./section";
import { MapsSettingsSection } from "./maps-settings";
import { SiteImport } from "../(data)/site-import";
import { resetDiscoveredNodes } from "./reset-discovered";

export function SettingsDialogContent({
  activeApp,
  more,
  children,
  hideAppSettings,
  withoutTraceLines = false,
  filters,
  mapNames,
}: {
  activeApp: string;
  more?: ReactNode;
  children?: ReactNode;
  hideAppSettings?: boolean;
  withoutTraceLines?: boolean;
  filters: FiltersConfig;
  /**
   * The game's map keys for the per-map settings section. Only needed when
   * this dialog is mounted OUTSIDE a CoordinatesProvider (e.g. the web
   * root-layout header) — inside one, the section reads them from context.
   */
  mapNames?: string[];
}) {
  const settingsStore = useSettingsStore();
  const profileSettings = useSettingsStore((state) => state);
  const theme = useTheme();
  const t = useT();
  return (
    <DialogContent
      onMouseDown={(e) => e.stopPropagation()}
      aria-describedby="settings-dialog-description"
    >
      <DialogHeader>
        <DialogTitle>
          {t("settings.title", { fallback: "Settings" })}
        </DialogTitle>
        <DialogDescription id="settings-dialog-description">
          {t("settings.description", {
            fallback: "Configure your application settings below.",
          })}
        </DialogDescription>
      </DialogHeader>
      <ScrollArea>
        <div className="space-y-3 pr-3">
          {more ? (
            <>
              {more}
              <Separator />
            </>
          ) : null}

          {/* Profiles */}
          <Section
            title={t("settings.profiles.title", { fallback: "Profiles" })}
            description={t("settings.profiles.description", {
              fallback:
                "Manage your settings profiles to quickly switch between different configurations.",
            })}
          >
            <ProfileManager activeApp={activeApp} />
          </Section>

          {/* Discovered Nodes */}
          <Section
            title={t("settings.discoveredNodes.title", {
              fallback: "Discovered Nodes",
            })}
            description={t("settings.discoveredNodes.description", {
              fallback: "You discovered {{count}} nodes.",
              vars: { count: String(settingsStore.discoveredNodes.length) },
            })}
          >
            <div className="flex items-center space-x-2">
              <Button
                size="sm"
                onClick={() => {
                  const fileName = `${activeApp}_discovered_nodes_${Date.now()}.json`;
                  if (typeof overwolf === "undefined") {
                    const blob = new Blob(
                      [JSON.stringify(settingsStore.discoveredNodes)],
                      {
                        type: "text/json",
                      },
                    );
                    saveFile(blob, fileName);
                  } else {
                    writeFileOverwolf(
                      JSON.stringify(settingsStore.discoveredNodes),
                      overwolf.io.paths.documents + "\\the-hidden-gaming-lair",
                      fileName,
                    );
                  }
                }}
              >
                {t("common.backup", { fallback: "Backup" })}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={async () => {
                  const file = await openFileOrFiles();
                  if (!file) {
                    return;
                  }
                  const reader = new FileReader();
                  reader.addEventListener("load", (loadEvent) => {
                    const text = loadEvent.target?.result;
                    if (!text || typeof text !== "string") {
                      return;
                    }
                    try {
                      let discoveredNodes = JSON.parse(text);
                      if (!Array.isArray(discoveredNodes)) {
                        discoveredNodes = [];
                      } else if (
                        discoveredNodes.some(
                          (node: unknown) => typeof node !== "string",
                        )
                      ) {
                        discoveredNodes = discoveredNodes.filter(
                          (node: unknown) => typeof node === "string",
                        );
                      }
                      settingsStore.setDiscoveredNodes(discoveredNodes);
                      toast.success(
                        t("discovered.restored", {
                          fallback: "Discovered nodes restored",
                        }),
                      );
                    } catch (error) {
                      // Do nothing
                    }
                  });
                  reader.readAsText(file);
                }}
              >
                {t("common.restore", { fallback: "Restore" })}
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => resetDiscoveredNodes(t)}
              >
                {t("common.reset", { fallback: "Reset" })}
              </Button>
            </div>
            {/* Import discovered markers from a third-party map site (renders
                only when a source covers this game). */}
            <SiteImport activeApp={activeApp} />
            <div className="flex items-center justify-between">
              <Label htmlFor="hide-discovered-nodes">
                {t("settings.hideDiscovered", {
                  fallback: "Hide Discovered Nodes",
                })}
              </Label>
              <Switch
                id="hide-discovered-nodes"
                checked={settingsStore.hideDiscoveredNodes}
                onCheckedChange={settingsStore.toggleHideDiscoveredNodes}
              />
            </div>
            <div className="flex items-center justify-between">
              <div className="pr-2">
                <Label htmlFor="auto-discover-collected">
                  {t("settings.autoDiscover", {
                    fallback: "Auto-discover collected items",
                  })}
                </Label>
                <p className="text-xs text-muted-foreground">
                  {t("settings.autoDiscover.description", {
                    fallback:
                      "Mark items you already collected in-game (e.g. effigies) as discovered automatically. Turning this off restores the ones it added.",
                  })}
                </p>
              </div>
              <Switch
                id="auto-discover-collected"
                checked={settingsStore.autoDiscoverCollected}
                onCheckedChange={settingsStore.setAutoDiscoverCollected}
              />
            </div>
            <div className="flex items-center justify-between">
              <div className="pr-2">
                <Label htmlFor="auto-reset-respawned">
                  {t("settings.autoResetRespawned", {
                    fallback: "Reset discovered nodes when they respawn",
                  })}
                </Label>
                <p className="text-xs text-muted-foreground">
                  {t("settings.autoResetRespawned.description", {
                    fallback:
                      "When the in-game app sees a discovered resource node (ore, trees, forage) spawn again, it is marked undiscovered. One-time finds like chests stay discovered.",
                  })}
                </p>
              </div>
              <Switch
                id="auto-reset-respawned"
                checked={settingsStore.autoResetRespawned}
                onCheckedChange={settingsStore.setAutoResetRespawned}
              />
            </div>
            <div className="flex items-center justify-between">
              <div className="pr-2">
                <Label htmlFor="flash-despawning-nodes">
                  {t("settings.flashDespawningNodes", {
                    fallback: "Flash nodes that are about to despawn",
                  })}
                </Label>
                <p className="text-xs text-muted-foreground">
                  {t("settings.flashDespawningNodes.description", {
                    fallback:
                      "When the in-game app sees a node's despawn countdown (e.g. Palia ore and forage), its marker flashes like it does in the game: slowly in the last 30 seconds, faster in the last 10.",
                  })}
                </p>
              </div>
              <Switch
                id="flash-despawning-nodes"
                checked={settingsStore.flashDespawningNodes}
                onCheckedChange={settingsStore.setFlashDespawningNodes}
              />
            </div>
            <div className="flex items-center justify-between">
              <div className="pr-2">
                <Label htmlFor="keep-collected-nodes-dimmed">
                  {t("settings.keepCollectedNodesDimmed", {
                    fallback: "Keep collected nodes on the map (faded)",
                  })}
                </Label>
                <p className="text-xs text-muted-foreground">
                  {t("settings.keepCollectedNodesDimmed.description", {
                    fallback:
                      "Ore you mined out, trees you chopped and forage you picked stay on the map, faded, until the game removes them, instead of disappearing right away. Needs the in-game app.",
                  })}
                </p>
              </div>
              <Switch
                id="keep-collected-nodes-dimmed"
                checked={settingsStore.keepCollectedNodesDimmed}
                onCheckedChange={settingsStore.setKeepCollectedNodesDimmed}
              />
            </div>
          </Section>

          {/* My Filters */}
          <Section
            title={t("settings.myFilters.title", { fallback: "My Filters" })}
            description={t("settings.myFilters.description", {
              fallback: "You have {{count}} filters.",
              vars: { count: String(settingsStore.myFilters.length) },
            })}
          >
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground">
                {t("settings.backupRestore", { fallback: "Backup & Restore" })}
              </p>
              <div className="flex items-center space-x-2">
                <Button
                  size="sm"
                  onClick={() => {
                    const fileName = `${activeApp}_My_Filters_${Date.now()}.json`;
                    if (typeof overwolf === "undefined") {
                      const blob = new Blob(
                        [JSON.stringify(settingsStore.myFilters)],
                        {
                          type: "text/json",
                        },
                      );
                      saveFile(blob, fileName);
                    } else {
                      writeFileOverwolf(
                        JSON.stringify(settingsStore.myFilters),
                        overwolf.io.paths.documents +
                          "\\the-hidden-gaming-lair",
                        fileName,
                      );
                    }
                  }}
                >
                  {t("settings.exportAll", { fallback: "Export All" })}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={async () => {
                    const file = await openFileOrFiles();
                    if (!file) {
                      return;
                    }
                    const reader = new FileReader();
                    reader.addEventListener("load", (loadEvent) => {
                      const text = loadEvent.target?.result;
                      if (!text || typeof text !== "string") {
                        return;
                      }
                      try {
                        let myFilters = JSON.parse(text);
                        if (!Array.isArray(myFilters)) {
                          myFilters = [];
                        }

                        settingsStore.setMyFilters(myFilters);
                        // setMyFilters is local-only by design (the sync path
                        // uses it to flip `synced` without re-triggering
                        // itself), so a restored backup would never reach the
                        // cloud. Adopt + upload anything lacking a server id.
                        settingsStore.adoptLocalOnlyFilters();
                        toast.success(
                          t("myFilters.restored", {
                            fallback: "My filters restored",
                          }),
                        );
                      } catch (error) {
                        // Do nothing
                      }
                    });
                    reader.readAsText(file);
                  }}
                >
                  {t("settings.importAll", { fallback: "Import All" })}
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => {
                    // resetMyFilters tombstones and server-deletes each one.
                    // setMyFilters([]) only cleared the array, so the rows
                    // survived and the next hydrate brought them all back.
                    settingsStore.resetMyFilters();
                    toast.warning(
                      t("myFilters.resetDone", {
                        fallback: "My filters reset",
                      }),
                    );
                  }}
                >
                  {t("common.reset", { fallback: "Reset" })}
                </Button>
              </div>
            </div>
          </Section>

          {/* Accessibility */}
          <Section
            title={t("settings.accessibility", { fallback: "Accessibility" })}
          >
            <div className="flex items-center gap-2 justify-between">
              <div>
                <Label htmlFor="theme">
                  {t("settings.theme", { fallback: "Theme" })}
                </Label>
                <p className="text-muted-foreground text-xs">
                  {t("settings.theme.description", {
                    fallback:
                      "Colors of the menus and pages on all THGL sites and apps.",
                  })}
                </p>
              </div>
              <Select
                value={theme}
                onValueChange={(value) => setTheme(value as Theme)}
              >
                <SelectTrigger id="theme" className="w-[180px] h-8 shrink-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="dark">
                    {t("settings.theme.dark", { fallback: "Dark" })}
                  </SelectItem>
                  <SelectItem value="light">
                    {t("settings.theme.light", { fallback: "Light" })}
                  </SelectItem>
                  <SelectItem value="black">
                    {t("settings.theme.black", { fallback: "Black (OLED)" })}
                  </SelectItem>
                  <SelectItem value="system">
                    {t("settings.theme.system", { fallback: "System" })}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Separator className="opacity-30" />
            <div className="flex items-center gap-2 justify-between">
              <Label htmlFor="color-blind-mode">
                {t("settings.colorBlindMode", { fallback: "Color Blind Mode" })}
              </Label>
              <Select
                value={profileSettings.colorBlindMode}
                onValueChange={settingsStore.setColorBlindMode}
              >
                <SelectTrigger className="w-[180px] h-8">
                  <SelectValue
                    placeholder={t("settings.selectMode", {
                      fallback: "Select mode",
                    })}
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">
                    {t("settings.colorBlind.none", { fallback: "None" })}
                  </SelectItem>
                  <SelectItem value="protanopia">
                    {t("settings.colorBlind.protanopia", {
                      fallback: "Protanopia",
                    })}
                  </SelectItem>
                  <SelectItem value="deuteranopia">
                    {t("settings.colorBlind.deuteranopia", {
                      fallback: "Deuteranopia",
                    })}
                  </SelectItem>
                  <SelectItem value="tritanopia">
                    {t("settings.colorBlind.tritanopia", {
                      fallback: "Tritanopia",
                    })}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2 justify-between">
              <Label htmlFor="color-blind-severity">
                {t("settings.colorBlindSeverity", {
                  fallback: "Color Blind Severity",
                })}
              </Label>
              <div className="flex items-center gap-2">
                <Slider
                  id="color-blind-severity"
                  className="w-40 h-8 p-0"
                  min={0}
                  max={1}
                  step={0.05}
                  value={[profileSettings.colorBlindSeverity]}
                  onValueChange={(values) => {
                    settingsStore.setColorBlindSeverity(values[0]);
                  }}
                />
                <span className="text-xs text-muted-foreground w-10 text-right">
                  {Math.round(profileSettings.colorBlindSeverity * 100)}%
                </span>
              </div>
            </div>
            <Separator className="opacity-30" />
            <div className="flex items-center gap-2 justify-between">
              <div>
                <Label htmlFor="map-darkness">
                  {t("settings.darkMap", { fallback: "Dark Map" })}
                </Label>
                <p className="text-muted-foreground text-xs">
                  {t("settings.darkMap.description", {
                    fallback:
                      "Darkens the map image. Light maps are inverted, dark maps are dimmed.",
                  })}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Slider
                  id="map-darkness"
                  className="w-40 h-8 p-0"
                  min={0}
                  max={1}
                  step={0.05}
                  value={[profileSettings.mapDarkness ?? 0]}
                  onValueChange={(values) => {
                    settingsStore.setMapDarkness(values[0]);
                  }}
                />
                <span className="text-xs text-muted-foreground w-10 text-right">
                  {Math.round((profileSettings.mapDarkness ?? 0) * 100)}%
                </span>
              </div>
            </div>
            <Separator className="opacity-30" />
            <div className="flex items-center justify-between">
              <div className="pr-2">
                <Label htmlFor="audio-alert-positional">
                  {t("settings.positionalAudio", {
                    fallback: "Positional audio alerts",
                  })}
                </Label>
                <p className="text-muted-foreground text-xs">
                  {t("settings.positionalAudio.description", {
                    fallback:
                      "Audio alerts get louder as you get closer and repeat while a marker is in range, with the tone panned to the side the marker is on. Made for playing by ear. Off = one alert when something enters range.",
                  })}
                </p>
              </div>
              <Switch
                id="audio-alert-positional"
                checked={profileSettings.audioAlertPositional ?? false}
                onCheckedChange={settingsStore.toggleAudioAlertPositional}
              />
            </div>
            <Separator className="opacity-30" />
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="high-contrast-mode">
                  {t("settings.highContrastMarkers", {
                    fallback: "High Contrast Markers",
                  })}
                </Label>
                <p className="text-muted-foreground text-xs">
                  {t("settings.highContrastMarkers.description", {
                    fallback:
                      "Adds a bright glow around markers for better visibility",
                  })}
                </p>
              </div>
              <Switch
                id="high-contrast-mode"
                checked={profileSettings.highContrastMode}
                onCheckedChange={settingsStore.toggleHighContrastMode}
              />
            </div>
            {profileSettings.highContrastMode && (
              <>
                <div className="flex items-center gap-2 justify-between">
                  <Label htmlFor="high-contrast-color">
                    {t("settings.outlineColor", { fallback: "Outline Color" })}
                  </Label>
                  <ColorPicker
                    id="high-contrast-color"
                    value={profileSettings.highContrastColor}
                    onChange={settingsStore.setHighContrastColor}
                  />
                </div>
                <div className="flex items-center gap-2 justify-between">
                  <Label htmlFor="high-contrast-thickness">
                    {t("settings.outlineThickness", {
                      fallback: "Outline Thickness",
                    })}
                  </Label>
                  <div className="flex items-center gap-2">
                    <Slider
                      id="high-contrast-thickness"
                      className="w-28 h-8 p-0"
                      min={1}
                      max={6}
                      step={1}
                      value={[profileSettings.highContrastThickness]}
                      onValueChange={(values) => {
                        settingsStore.setHighContrastThickness(values[0]);
                      }}
                    />
                    <span className="text-xs text-muted-foreground w-10 text-right">
                      {profileSettings.highContrastThickness}px
                    </span>
                  </div>
                </div>
              </>
            )}
          </Section>

          {/* Icon Sizes & Map Behavior */}
          <Section title={t("settings.iconSizes", { fallback: "Icon Sizes" })}>
            <IconSizes filters={filters} />
            <div className="flex items-center justify-between mt-3">
              <Label htmlFor="dynamic-icon-size">
                {t("settings.dynamicIconSize", {
                  fallback: "Dynamic icon size (zoom-based)",
                })}
              </Label>
              <Switch
                id="dynamic-icon-size"
                checked={profileSettings.dynamicIconSize}
                onCheckedChange={settingsStore.toggleDynamicIconSize}
              />
            </div>
            {profileSettings.dynamicIconSize && (
              <div className="flex items-center gap-2 justify-between mt-2">
                <Label htmlFor="dynamic-icon-factor">
                  {t("settings.factor", { fallback: "Factor" })}
                </Label>
                <Slider
                  id="dynamic-icon-factor"
                  min={0.1}
                  max={1}
                  step={0.05}
                  value={[profileSettings.dynamicIconSizeFactor]}
                  onValueChange={([v]) =>
                    settingsStore.setDynamicIconSizeFactor(v)
                  }
                  className="w-32"
                />
              </div>
            )}
            <div className="flex items-center justify-between mt-3">
              <Label htmlFor="player-below-markers">
                {t("settings.playerBelowMarkers", {
                  fallback: "Player icon below markers",
                })}
              </Label>
              <Switch
                id="player-below-markers"
                checked={profileSettings.playerBelowMarkers}
                onCheckedChange={settingsStore.togglePlayerBelowMarkers}
              />
            </div>
            <div className="flex items-center gap-2 justify-between mt-3">
              <Label htmlFor="player-icon-color">
                {t("settings.playerIconColor", {
                  fallback: "Player icon color",
                })}
              </Label>
              {/* Empty ("Clear color") keeps the game's own icon colors. */}
              <ColorPicker
                id="player-icon-color"
                value={profileSettings.playerIconColor ?? ""}
                onChange={settingsStore.setPlayerIconColor}
              />
            </div>
          </Section>

          {activeApp === "palia" && (
            <Section title={t("settings.palia", { fallback: "Palia" })}>
              <div className="flex items-center justify-between">
                <Label htmlFor="world-code-requests">
                  {t("settings.worldCodeRequests", {
                    fallback: "Join-code request notifications",
                  })}
                </Label>
                <Switch
                  id="world-code-requests"
                  checked={!profileSettings.worldCodeRequestsMuted}
                  onCheckedChange={(on) =>
                    settingsStore.setWorldCodeRequestsMuted(!on)
                  }
                />
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                {t("settings.worldCodeRequestsHint", {
                  fallback:
                    "Get a prompt when another player wants to join your world, so you can share your join code. Turn off to hide these.",
                })}
              </p>
            </Section>
          )}

          {isOverwolf && (
            <Section title={t("settings.privacy", { fallback: "Privacy" })}>
              <div className="flex items-center justify-between">
                <Label htmlFor="help-improve-maps">
                  {t("settings.helpImproveMaps", {
                    fallback: "Help improve maps (anonymous)",
                  })}
                </Label>
                <Switch
                  id="help-improve-maps"
                  checked={profileSettings.helpImproveMaps !== false}
                  onCheckedChange={settingsStore.setHelpImproveMaps}
                />
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                {t("settings.helpImproveMapsDesc", {
                  fallback:
                    "To fix map problems, the app may send what it detects at the affected spot (object types and positions). Never your name, account or other players.",
                })}
              </p>
            </Section>
          )}

          {!hideAppSettings && (
            <>
              {/* Map Behavior */}
              <Section
                title={t("settings.mapBehavior", { fallback: "Map Behavior" })}
              >
                <div className="flex items-center justify-between">
                  <Label htmlFor="zoom-on-filter-change">
                    {t("settings.zoomOnFilterChange", {
                      fallback: "Zoom map on filters change",
                    })}
                  </Label>
                  <Switch
                    id="zoom-on-filter-change"
                    checked={profileSettings.fitBoundsOnChange}
                    onCheckedChange={settingsStore.toggleFitBoundsOnChange}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div className="pr-2">
                    <Label htmlFor="rotate-map-with-ctrl-drag">
                      {t("settings.rotateMapWithCtrlDrag", {
                        fallback: "Rotate map with Ctrl + drag",
                      })}
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      {t("settings.rotateMapWithCtrlDrag.description", {
                        fallback:
                          "Holding Ctrl while dragging rotates and tilts the map. Turn it off if Ctrl is your push-to-talk key: Ctrl + drag then pans like a normal drag. Middle-mouse drag still rotates.",
                      })}
                    </p>
                  </div>
                  <Switch
                    id="rotate-map-with-ctrl-drag"
                    checked={profileSettings.rotateMapWithCtrlDrag ?? true}
                    onCheckedChange={settingsStore.setRotateMapWithCtrlDrag}
                  />
                </div>
                {/* Only the app overlay has "Hide Controls" (inbox #1463). */}
                {isApp && (
                  <div className="flex items-center justify-between">
                    <div className="pr-2">
                      <Label htmlFor="fade-locked-map-on-hover">
                        {t("settings.fadeLockedMapOnHover", {
                          fallback:
                            "Fade map on hover while controls are hidden",
                        })}
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        {t("settings.fadeLockedMapOnHover.description", {
                          fallback:
                            "While Hide Controls is on, the overlay map turns almost transparent when your mouse is over it, so you can see the game behind it. Turn it off to keep the map fully visible.",
                        })}
                      </p>
                    </div>
                    <Switch
                      id="fade-locked-map-on-hover"
                      checked={profileSettings.fadeLockedMapOnHover ?? true}
                      onCheckedChange={settingsStore.setFadeLockedMapOnHover}
                    />
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <div className="pr-2">
                    <Label htmlFor="show-interior-label-names">
                      {t("settings.showInteriorNames", {
                        fallback: "Show interior names on the map",
                      })}
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      {t("settings.showInteriorNames.description", {
                        fallback:
                          "The name next to the layer button of buildings and underground areas. Turn it off for a cleaner map - the button stays, so you can still open their floor maps.",
                      })}
                    </p>
                  </div>
                  <Switch
                    id="show-interior-label-names"
                    checked={profileSettings.showInteriorLabelNames ?? true}
                    onCheckedChange={settingsStore.setShowInteriorLabelNames}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div className="pr-2">
                    <Label htmlFor="show-spawn-shapes">
                      {t("settings.showSpawnShapes", {
                        fallback: "Show object outlines",
                      })}
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      {t("settings.showSpawnShapes.description", {
                        fallback:
                          "Draws the shape of containers, traps and other objects that have one, like the game highlights them, and the area around a marker whose spot changes each time. Discovered ones lose their outline.",
                      })}
                    </p>
                  </div>
                  <Switch
                    id="show-spawn-shapes"
                    checked={profileSettings.showSpawnShapes ?? true}
                    onCheckedChange={settingsStore.setShowSpawnShapes}
                  />
                </div>
              </Section>

              {/* Per-Map Settings (all surfaces; null for single-map games) */}
              <MapsSettingsSection mapNames={mapNames} />

              {/* Trace Line */}
              <Section
                title={t("settings.traceLine", { fallback: "Trace Line" })}
                description={
                  withoutTraceLines
                    ? t("settings.traceLine.unsupported", {
                        fallback: "This game does not support trace lines.",
                      })
                    : undefined
                }
              >
                {!withoutTraceLines && (
                  <>
                    <div className="flex items-center justify-between">
                      <Label htmlFor="show-trace-line">
                        {t("settings.showTraceLine", {
                          fallback: "Show Trace Line",
                        })}
                      </Label>
                      <Switch
                        id="show-trace-line"
                        checked={profileSettings.showTraceLine}
                        onCheckedChange={settingsStore.toggleShowTraceLine}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label htmlFor="follow-player">
                        {t("settings.followPlayer", {
                          fallback: "Follow Player",
                        })}
                      </Label>
                      <Switch
                        id="follow-player"
                        checked={profileSettings.followPlayer}
                        onCheckedChange={settingsStore.toggleFollowPlayer}
                      />
                    </div>
                    <div className="space-y-2">
                      <div>
                        <Label>
                          {t("settings.rotateMapWithPlayer", {
                            fallback: "Rotate Map With Player",
                          })}
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          {t("settings.rotateMapWithPlayer.description", {
                            fallback:
                              "Turns the map so the direction you are facing is always up, like a rotating minimap. Turns on Follow Player as well.",
                          })}
                        </p>
                      </div>
                      {/* One value per window: the in-game overlay minimap
                          (apps only) and the desktop / website map. */}
                      {isApp && (
                        <div className="flex items-center justify-between gap-2 pl-2">
                          <Label
                            htmlFor="rotate-map-with-player-overlay"
                            className="font-normal"
                          >
                            {t("settings.rotateMapWithPlayer.overlay", {
                              fallback: "In-game overlay",
                            })}
                          </Label>
                          <Switch
                            id="rotate-map-with-player-overlay"
                            checked={
                              (profileSettings.rotateMapWithPlayerOverlay ??
                                false) &&
                              profileSettings.followPlayer
                            }
                            onCheckedChange={(checked) =>
                              settingsStore.setRotateMapWithPlayer(
                                checked,
                                "overlay",
                              )
                            }
                          />
                        </div>
                      )}
                      <div className="flex items-center justify-between gap-2 pl-2">
                        <Label
                          htmlFor="rotate-map-with-player"
                          className="font-normal"
                        >
                          {t("settings.rotateMapWithPlayer.desktop", {
                            fallback: isApp
                              ? "Desktop window and website"
                              : "Website map",
                          })}
                        </Label>
                        <Switch
                          id="rotate-map-with-player"
                          checked={
                            (profileSettings.rotateMapWithPlayer ?? false) &&
                            profileSettings.followPlayer
                          }
                          onCheckedChange={(checked) =>
                            settingsStore.setRotateMapWithPlayer(
                              checked,
                              "desktop",
                            )
                          }
                        />
                      </div>
                    </div>
                    <div className="flex items-center gap-2 justify-between">
                      <Label htmlFor="trace-line-length">
                        {t("settings.traceLineLength", {
                          fallback: "Trace Line Length",
                        })}
                      </Label>
                      <Input
                        type="number"
                        id="trace-line-length"
                        value={profileSettings.traceLineLength}
                        className="w-fit"
                        onChange={(event) =>
                          settingsStore.setTraceLineLength(+event.target.value)
                        }
                        min={0}
                      />
                    </div>
                    <div className="flex items-center gap-2 justify-between">
                      <Label htmlFor="trace-line-rate">
                        {t("settings.traceLineRate", {
                          fallback: "Trace Line Rate",
                        })}
                      </Label>
                      <Input
                        type="number"
                        id="trace-line-rate"
                        value={profileSettings.traceLineRate}
                        className="w-fit"
                        onChange={(event) =>
                          settingsStore.setTraceLineRate(+event.target.value)
                        }
                        min={0}
                      />
                    </div>
                    <div className="flex items-center gap-2 justify-between">
                      <Label htmlFor="trace-line-color">
                        {t("settings.traceLineColor", {
                          fallback: "Trace Line Color",
                        })}
                      </Label>
                      <ColorPicker
                        id="trace-line-color"
                        value={profileSettings.traceLineColor}
                        onChange={settingsStore.setTraceLineColor}
                      />
                    </div>
                    <div className="flex items-center gap-2 justify-between">
                      <Label htmlFor="trace-line-style">
                        {t("settings.traceLineStyle", {
                          fallback: "Trace Line Style",
                        })}
                      </Label>
                      <Select
                        value={profileSettings.traceLineStyle}
                        onValueChange={(v) =>
                          settingsStore.setTraceLineStyle(v as "dots" | "line")
                        }
                      >
                        <SelectTrigger className="w-24" id="trace-line-style">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="dots">
                            {t("settings.traceLineDots", { fallback: "Dots" })}
                          </SelectItem>
                          <SelectItem value="line">
                            {t("settings.traceLineLine", { fallback: "Line" })}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </>
                )}
              </Section>

              {/* Proximity Range */}
              <Section
                title={t("settings.proximityRange", {
                  fallback: "Proximity Range",
                })}
                description={t("settings.proximityRange.description", {
                  fallback:
                    'Used by audio alerts, "In Range" labels, and "Discover Nearest Node". Configure per-filter using the settings icon next to each filter.',
                })}
              >
                <div className="space-y-1">
                  <div className="flex items-center gap-2 justify-between">
                    <Label htmlFor="proximity-range">
                      {t("settings.range", { fallback: "Range" })}
                    </Label>
                    <Input
                      type="number"
                      id="proximity-range"
                      value={profileSettings.audioAlertRange}
                      className="w-24"
                      onChange={(e) =>
                        settingsStore.setAudioAlertRange(+e.target.value)
                      }
                      min={10}
                    />
                  </div>
                  <p className="text-muted-foreground text-xs">
                    {t("settings.range.description", {
                      fallback:
                        "Distance in map units. The ideal value depends on the game and map scale.",
                    })}
                  </p>
                </div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="show-proximity-range">
                    {t("settings.showRangeOnMap", {
                      fallback: "Show Range on Map",
                    })}
                  </Label>
                  <Switch
                    id="show-proximity-range"
                    checked={profileSettings.showAudioAlertRange}
                    onCheckedChange={settingsStore.toggleShowAudioAlertRange}
                  />
                </div>
              </Section>

              {/* Audio Alerts */}
              <Section
                title={t("settings.audioAlerts", { fallback: "Audio Alerts" })}
                description={t("settings.audioAlerts.description", {
                  fallback:
                    "Play a sound when tracked items appear within range. Enable per-filter using the settings icon next to each filter.",
                })}
              >
                <div className="flex items-center justify-between">
                  <Label htmlFor="mute-audio-alerts">
                    {t("settings.muteAll", { fallback: "Mute All" })}
                  </Label>
                  <Switch
                    id="mute-audio-alerts"
                    checked={profileSettings.audioAlertsMuted}
                    onCheckedChange={settingsStore.toggleAudioAlertsMuted}
                  />
                </div>
                <div className="flex items-center justify-between gap-2">
                  <Label htmlFor="audio-alert-notifications">
                    {t("settings.showNotification", {
                      fallback: "Show Notification",
                    })}
                    <span className="block text-muted-foreground text-xs font-normal">
                      {t("settings.showNotification.description", {
                        fallback:
                          "Pop up which alert fired, so you know what dinged.",
                      })}
                    </span>
                  </Label>
                  <Switch
                    id="audio-alert-notifications"
                    checked={profileSettings.audioAlertNotifications}
                    onCheckedChange={
                      settingsStore.toggleAudioAlertNotifications
                    }
                  />
                </div>
                {(() => {
                  // List the filters that currently have an audio alert set,
                  // so it's easy to remember what you turned on (and silence a
                  // stray one) instead of getting dinged with no idea why.
                  const enabledIds = Object.entries(
                    profileSettings.audioAlertByFilter,
                  )
                    .filter(([, on]) => on)
                    .map(([id]) => id);
                  return (
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between gap-2">
                        <Label>
                          {t("settings.activeAlerts", {
                            fallback: "Active Alerts",
                          })}
                          <span className="text-muted-foreground font-normal">
                            {" "}
                            ({enabledIds.length})
                          </span>
                        </Label>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-8"
                          disabled={enabledIds.length === 0}
                          onClick={settingsStore.resetAudioAlerts}
                        >
                          {t("settings.resetAll", { fallback: "Reset all" })}
                        </Button>
                      </div>
                      {enabledIds.length === 0 ? (
                        <p className="text-muted-foreground text-xs">
                          {t("settings.noAudioAlerts", {
                            fallback:
                              "No audio alerts set. Enable one with the gear icon next to a filter.",
                          })}
                        </p>
                      ) : (
                        // Native overflow scroll — Radix ScrollArea needs a
                        // definite height to scroll, and a max-height-only list
                        // wouldn't (the viewport never overflows). This scrolls
                        // reliably regardless of item count.
                        <ul className="max-h-48 space-y-0.5 overflow-y-auto pr-1 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-ring/50 [&::-webkit-scrollbar-track]:bg-transparent">
                          {enabledIds.map((id) => {
                            const name = t(id) || id;
                            return (
                              <li
                                key={id}
                                className="flex items-center justify-between gap-2 rounded px-1.5 py-1 text-sm hover:bg-muted/50"
                              >
                                <span className="truncate">{name}</span>
                                <button
                                  type="button"
                                  aria-label={t("settings.turnOffAlertFor", {
                                    fallback: "Turn off alert for {{name}}",
                                    vars: { name },
                                  })}
                                  title={t("settings.turnOffAlert", {
                                    fallback: "Turn off this alert",
                                  })}
                                  className="shrink-0 text-muted-foreground hover:text-destructive transition-colors"
                                  onClick={() =>
                                    settingsStore.toggleAudioAlertByFilter(id)
                                  }
                                >
                                  <X className="h-3.5 w-3.5" />
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  );
                })()}
                <div className="flex items-center gap-2 justify-between">
                  <div className="pr-2">
                    <Label htmlFor="audio-alert-sound">
                      {t("settings.alertSound", { fallback: "Alert Sound" })}
                    </Label>
                    <p className="text-muted-foreground text-xs">
                      {t("settings.alertSound.description", {
                        fallback:
                          "Used by filters that don't have their own tone. Set a tone per filter with the gear icon next to it.",
                      })}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Select
                      value={profileSettings.audioAlertSound}
                      onValueChange={settingsStore.setAudioAlertSound}
                    >
                      <SelectTrigger className="w-28 h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ALERT_SOUND_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      size="icon"
                      variant="outline"
                      className="h-8 w-8"
                      onClick={() =>
                        playAlertSound(
                          profileSettings.audioAlertSound,
                          profileSettings.audioAlertVolume,
                        )
                      }
                      title={t("settings.previewSound", {
                        fallback: "Preview sound",
                      })}
                    >
                      <Play className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
                <div className="flex items-center gap-2 justify-between">
                  <Label htmlFor="audio-alert-volume">
                    {t("settings.volume", { fallback: "Volume" })}
                  </Label>
                  <div className="flex items-center gap-2">
                    <Slider
                      id="audio-alert-volume"
                      className="w-28 h-8 p-0"
                      min={0}
                      max={1}
                      step={0.1}
                      value={[profileSettings.audioAlertVolume]}
                      onValueChange={(values) => {
                        settingsStore.setAudioAlertVolume(values[0]);
                      }}
                    />
                    <span className="text-xs text-muted-foreground w-10 text-right">
                      {Math.round(profileSettings.audioAlertVolume * 100)}%
                    </span>
                  </div>
                </div>
              </Section>

              {/* Marker Labels */}
              <Section
                title={t("settings.markerLabels", {
                  fallback: "Marker Labels",
                })}
                description={t("settings.markerLabels.description", {
                  fallback:
                    "Show text labels above markers for easier identification. Configure per-filter using the settings icon next to each filter.",
                })}
              >
                <div className="flex items-center gap-2 justify-between">
                  <Label htmlFor="label-text-size">
                    {t("settings.labelTextSize", {
                      fallback: "Label Text Size",
                    })}
                  </Label>
                  <div className="flex items-center gap-2">
                    <Slider
                      id="label-text-size"
                      className="w-28 h-8 p-0"
                      min={0.5}
                      max={2}
                      step={0.1}
                      value={[profileSettings.labelTextSize]}
                      onValueChange={(values) => {
                        settingsStore.setLabelTextSize(values[0]);
                      }}
                    />
                    <span className="text-xs text-muted-foreground w-10 text-right">
                      {profileSettings.labelTextSize.toFixed(1)}x
                    </span>
                  </div>
                </div>
              </Section>
            </>
          )}

          {children}

          {/* Reset Interface — always at the bottom */}
          <Separator />
          <div className="flex items-center justify-between py-1">
            <div>
              <p className="text-sm font-medium">
                {t("settings.resetInterface", { fallback: "Reset Interface" })}
              </p>
              <p className="text-muted-foreground text-xs">
                {t("settings.resetInterface.description", {
                  fallback: "Resets all visual settings to defaults",
                })}
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={() => {
                settingsStore.resetInterface();
                toast.success(
                  t("settings.interfaceReset", {
                    fallback: "Interface reset to defaults",
                  }),
                );
              }}
            >
              <RotateCcw className="h-3.5 w-3.5" />
              {t("common.reset", { fallback: "Reset" })}
            </Button>
          </div>
        </div>
      </ScrollArea>
    </DialogContent>
  );
}
